import { Trans } from 'react-i18next';
import React from 'react';

/**
 * Contingent.
 *
 * @param {Object} params - Object parameter
 * @param {string} params.name - Name of the contingent
 * @param {boolean} params.seated - When true, the contingent is bound to a sea
 * @param {string} params.uuid - Unique identifier
 * @param {string} params.color - Hex representation of the contingent color
 * @param {Array<Object>} params.rates - List of rates included in the contingent
 * @constructor
 */
function Contingent({ name, seated = false, uuid: id, capacity, color, rates, booked, booking_in_progress, has_bookings }) {
    let that = {};

    that.booked = () => booked;
    that.bookingInProgress = () => booking_in_progress;
    that.name = () => name;
    that.id = () => id;
    that.seated = () => seated;
    that.capacity = () => capacity;
    that.color = () => color;
    that.rates = () => rates;
    that.hasBookings = () => has_bookings;

    that.deletable = () => (has_bookings === undefined ? false : !that.hasBookings());

    that.hasRates = () => !!that.rates().length;

    that.toJSON = () => ({
        uuid: id,
        capacity,
        color,
        name,
        rates,
        seated
    });

    that.equals = contingent => JSON.stringify(this.toJSON()) === JSON.stringify(contingent.toJSON());

    return that;
}

/**
 * Simple sort function that sorts an array of Contingents by alphabtical order on their names
 * @param {Contingent[]} contingents - List of contingents to sort
 * @returns {Contingent[]} List of sorted contingents
 */
Contingent.sortByName = contingents => {
    contingents.sort((contingentA, contingentB) => {
        if (contingentA.name < contingentB.name) return -1;
        if (contingentA.name > contingentB.name) return 1;
        return 0;
    });
    return contingents;
};

/**
 * Produces an object containing Contingents mapped by their id.
 * @param {Object[]} contingents - List of API-returned contingents
 * @returns {Object[]} Contingent instances
 */
Contingent.fromAPI = contingents =>
    contingents.reduce((acc, contingent) => {
        acc[contingent.uuid] = new Contingent(contingent);
        return acc;
    }, {});

export const NULL_CONTIGENT_ID = 'no-contingent';

/**
 * Null object pattern for Contingent.
 * @constructor
 */
export class NullContingent {
    name() {
        return <Trans i18nKey="contingent.none">No contingent</Trans>;
    }
    id() {
        return NULL_CONTIGENT_ID;
    }
    seated() {
        return false;
    }
    capacity() {
        return 0;
    }
    color() {
        return 'steelblue';
    }
    rates() {
        return [];
    }
}

export default Contingent;
